/** SBOM import, demo generation and continuous Dependency-Track sync. */
import { Trans, useLingui } from '@lingui/react/macro';
import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Banner } from '../../components/Banner';
import { errorMessage, fetchChangeStatus } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import XDomeSbomCard from '../admin/XDomeSbomCard';

export function SystemSbomPage(): ReactNode {
  const { t } = useLingui();
  const { has } = useAuth();
  const [status, setStatus] = useState<Awaited<ReturnType<typeof fetchChangeStatus>> | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let active = true;
    let timer: ReturnType<typeof setTimeout>;
    async function refresh(): Promise<void> {
      try {
        const next = await fetchChangeStatus();
        if (active) { setStatus(next); setError(null); }
      } catch (err) {
        if (active) setError(errorMessage(err));
      } finally {
        if (active) timer = setTimeout(() => void refresh(), 5000);
      }
    }
    void refresh();
    return () => { active = false; clearTimeout(timer); };
  }, []);
  const retryNote = (attempts: number): string =>
    t`Übertragung fehlgeschlagen; automatischer Wiederholungsversuch folgt. Bisherige Versuche: ${attempts}.`;
  return (
    <>
      <XDomeSbomCard />
      {has('import.write') ? (
        <div className="card">
          <div className="card-head"><h2><Trans>SBOM in Stammdaten übernehmen</Trans></h2></div>
          <div className="card-body">
            <p>
              <Trans>
                CycloneDX-JSON hochladen und Komponenten sowie enthaltene Demo-Maschinen, Kunden und Auslieferungen in
                CMDBuild übernehmen.
              </Trans>
            </p>
            <Link to="/import"><Trans>SBOM importieren</Trans></Link>
          </div>
        </div>
      ) : null}
      <div className="card">
        <div className="card-head"><h2><Trans>Automatischer Dependency-Track-Sync</Trans></h2></div>
        <div className="card-body">
          <p><Trans>Neue und geänderte ausgelieferte Maschinen werden automatisch an Dependency-Track übertragen,
            ebenso Änderungen an ihren Komponenten und Stücklisten.</Trans></p>
          <p><Trans>Änderungen aus der Oberfläche und aus Importen starten den Abgleich automatisch. Direkte Änderungen
            in CMDBuild werden im Abstand von 60 Sekunden geprüft, beim Serverstart der gesamte Bestand.</Trans></p>
          <div aria-live="polite">
            {error ? <Banner kind="error"><Trans>Sync-Status nicht verfügbar: {error}</Trans></Banner> :
              !status ? <p><Trans>Status wird geladen…</Trans></p> :
              status.poll_failed ? <Banner kind="warn"><Trans>CMDBuild konnte nicht geprüft werden. Die Prüfung wird automatisch wiederholt.</Trans></Banner> :
              status.failed ? <Banner kind="warn">{retryNote(status.attempts)}</Banner> :
              <p>{status.pending ? t`Änderungen warten auf Übertragung oder werden verarbeitet.` : t`Automatik aktiv · keine Übertragung ausstehend.`}</p>}
          </div>
          <p className="muted">
            <Trans>
              Je ausgelieferte Maschine entsteht ein Projekt in Dependency-Track. Produkte ohne Auslieferung oder ohne
              auflösbare Komponenten werden nicht übertragen.
            </Trans>
          </p>
        </div>
      </div>
    </>
  );
}
export default SystemSbomPage;
